import React, { useState, useContext, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { UserApiExtended, UserDocumentDto } from '../../api/UserApi';

import "./UserDocumentsView.scss";
import Nav from "../../components/Nav/Nav";

const UserDocumentsView = () => {
  const [documents, setDocuments] = useState<UserDocumentDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDoc, setSelectedDoc] = useState<UserDocumentDto | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<UserDocumentDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Fetch user documents
  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);

    try {
      const userDocuments = await UserApiExtended.getUserDocuments();
      setDocuments(userDocuments);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  // Upload document
  const handleDocumentUpload = async (file: File) => {
    setUploading(true);
    setError(null);

    try {
      await UserApiExtended.uploadUserDocument(file);
      setSuccess('Document uploaded successfully');
      fetchDocuments(); // Refresh the documents list
    } catch (err) {
      console.error('Error uploading document:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  // Helper function to check if file is an image
  const isImageFile = (fileName: string): boolean => {
    const imageExtensions = ['.png', '.jpg', '.jpeg'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return imageExtensions.includes(extension);
  };

  // Helper function to handle file click
  const handleFileClick = (doc: UserDocumentDto) => {
    const fileUrl = `${process.env.API_HOST || ''}/api/v1/documents/${doc.guid}`;
    
    if (isImageFile(doc.name)) {
      // For images, open in new tab for viewing
      window.open(fileUrl, '_blank');
    } else {
      // For other files, trigger download
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Handle download button click (force download even for images)
  const handleDownload = async (doc: UserDocumentDto) => {
    try {
      const fileUrl = `${process.env.API_HOST || ''}/api/v1/documents/${doc.guid}`;
      
      if (isImageFile(doc.name)) {
        // For images, fetch as blob to ensure download
        const response = await fetch(fileUrl, {
          credentials: 'include',
        });
        
        if (!response.ok) {
          throw new Error('Failed to download file');
        }
        
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        // For non-image files, direct download
        window.open(fileUrl, '_blank');
      }
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Failed to download file');
    }
  };

  // Handle menu open
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, doc: UserDocumentDto) => {
    setAnchorEl(event.currentTarget);
    setSelectedDoc(doc);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDoc(null);
  };

  // Handle delete confirmation dialog
  const handleDeleteClick = () => {
    if (selectedDoc) {
      setDocumentToDelete(selectedDoc);
      setDeleteDialogOpen(true);
      handleMenuClose();
    }
  };

  // Handle delete document
  const handleDeleteDocument = async () => {
    if (!documentToDelete) return;
    
    setDeleting(true);
    setError(null);

    try {
      await UserApiExtended.deleteUserDocument(documentToDelete.guid);
      setSuccess('Document deleted successfully');
      fetchDocuments(); // Refresh the documents list
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete document');
    } finally {
      setDeleting(false);
    }
  };

  // Handle download from menu
  const handleDownloadFromMenu = () => {
    if (selectedDoc) {
      handleDownload(selectedDoc);
      handleMenuClose();
    }
  };

  // Load documents on component mount
  useEffect(() => {
    fetchDocuments();
  }, []);

  // Close success message
  const handleCloseSuccess = () => {
    setSuccess(null);
  };

  // Close error message
  const handleCloseError = () => {
    setError(null);
  };

  return (
    <>
      <Nav />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center' }}>
              <AttachFileIcon sx={{ mr: 1 }} />
              My Documents ({documents.length})
            </Typography>
            <Button 
              variant="contained"
              startIcon={<UploadFileIcon />}
              component="label"
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload File'}
              <input
                type="file"
                hidden
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleDocumentUpload(file);
                    e.target.value = ''; // Reset input
                  }
                }}
              />
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : documents.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No documents uploaded yet
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Upload your first document using the "Upload File" button above.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>File Name</TableCell>
                    <TableCell>Upload Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AttachFileIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          {isImageFile(doc.name) ? (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                cursor: 'pointer', 
                                color: 'primary.main',
                                textDecoration: 'underline',
                                '&:hover': {
                                  color: 'primary.dark'
                                }
                              }}
                              onClick={() => handleFileClick(doc)}
                              title="Click to view image"
                            >
                              {doc.name}
                            </Typography>
                          ) : (
                            <Typography 
                              variant="body2"
                              sx={{ 
                                cursor: 'pointer', 
                                color: 'primary.main',
                                textDecoration: 'underline',
                                '&:hover': {
                                  color: 'primary.dark'
                                }
                              }}
                              onClick={() => handleFileClick(doc)}
                              title="Click to download"
                            >
                              {doc.name}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {new Date(doc.uploadedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, doc)}
                          title="More actions"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleDownloadFromMenu}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!deleting) {
            setDeleteDialogOpen(false);
            setDocumentToDelete(null);
          }
        }}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Document
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete "{documentToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setDocumentToDelete(null);
            }} 
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteDocument} 
            color="error" 
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};
export default UserDocumentsView;